import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { IdLabelValue } from '@types';
import { getLocalStorageItem } from '@utils/storage';

import { useLazyGetAdsQuery } from '../queries/LeadManagementQuery';

const ADS_PAGE_LIMIT = 10;

export type UseAdsFilterResult = {
  adOptions: IdLabelValue[];
  fetchAds: (searchTerm?: string, reset?: boolean) => void;
  loadMoreAds: () => void;
  adsHasMore: boolean;
  adsIsLoading: boolean;
  adsTotalCount: number;
};

const useAdsFilter = (): UseAdsFilterResult => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const pageRef = useRef(0);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const searchTermRef = useRef('');

  const [accumulatedAds, setAccumulatedAds] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [hasMore, setHasMoreState] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const setHasMore = (value: boolean) => {
    hasMoreRef.current = value;
    setHasMoreState(value);
  };

  const [triggerGetAds, { isFetching: isLoading }] = useLazyGetAdsQuery();

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const fetchAds = useCallback(
    async (searchTerm?: string, reset = false) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      if (reset) {
        searchTermRef.current = searchTerm || '';
      }

      const page = reset ? 0 : pageRef.current;
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(page));
      queryParams.set('limit', String(ADS_PAGE_LIMIT));
      if (searchTerm) {
        queryParams.set('search', searchTerm);
      }

      try {
        const result = await triggerGetAds({
          organizationId,
          queryString: `?${queryParams.toString()}`
        }).unwrap();

        const newAds = result?.data || [];
        const more = !result?.pagination?.last_page;

        if (result?.pagination?.total_rows != null) {
          setTotalCount(result.pagination.total_rows);
        }

        if (reset) {
          setAccumulatedAds(newAds);
          pageRef.current = 1;
        } else {
          setAccumulatedAds((prev) => [...prev, ...newAds]);
          pageRef.current += 1;
        }

        setHasMore(more);
      } catch {
        setHasMore(false);
      }
    },
    [organizationId, triggerGetAds]
  );

  const loadMoreAds = useCallback(() => {
    if (!isLoadingRef.current && hasMoreRef.current) {
      fetchAds(searchTermRef.current);
    }
  }, [fetchAds]);

  const adOptions: IdLabelValue[] = useMemo(
    () =>
      accumulatedAds.map((ad) => ({
        id: String(ad.id),
        label: String(ad.name ?? ''),
        value: String(ad.id)
      })),
    [accumulatedAds]
  );

  return {
    adOptions,
    fetchAds,
    loadMoreAds,
    adsHasMore: hasMore,
    adsIsLoading: isLoading,
    adsTotalCount: totalCount
  };
};

export default useAdsFilter;
